import { FC, Fragment, useCallback } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton } from "@mui/material";
import { ConfirmationDialog } from "@/components/common/modals/ConfirmationDialog";
import { useDialog } from "@/utils/hooks/useDialog.hook";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateDashboardDimensionFilterThunk } from "@/store/dashboards/thunks/dashboard/updateDashboardDimensionFilterThunk";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";
import { dashboardDimensionsCategoryIdsByDashboardIdSelector } from "@/store/dashboards/selectors/dashboard/filters/dashboardDimensionsCategoryIdsByDashboardIdSelector";

interface DashboardDimensionFilterRemoveButtonProps {
  dashboardId: string;
  onRemove(): void;
}

export const DashboardDimensionFilterRemoveButton: FC<
  DashboardDimensionFilterRemoveButtonProps
> = ({ onRemove, dashboardId }) => {
  const dispatch = useAppDispatch();
  const isProtected = useAppSelector(currentDashboardProtectedSelector);
  const dimensionCategoryIds = useAppSelector((state) =>
    dashboardDimensionsCategoryIdsByDashboardIdSelector(state, dashboardId),
  );

  const atLeastOneSelected = !!(
    dimensionCategoryIds?.dimension_id || dimensionCategoryIds?.category_id
  );

  const { open, openDialog, closeDialog } = useDialog();

  const confirmHandler = useCallback(() => {
    onRemove();

    dispatch(
      updateDashboardDimensionFilterThunk({
        dashboardId,
        category_id: null,
        dimension_id: null,
      }),
    );
  }, [dispatch, dashboardId, onRemove]);

  return (
    <Fragment>
      <IconButton
        size="small"
        onClick={atLeastOneSelected ? openDialog : onRemove}
        disabled={isProtected}
        sx={{ justifySelf: "end" }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>

      <ConfirmationDialog
        open={open}
        title="Delete Filters?"
        dialogContent="Are you sure you want to delete dimension and category filters?"
        CancelButtonProps={{ onClick: closeDialog }}
        ConfirmButtonProps={{
          children: "Delete",
          onClick: confirmHandler,
        }}
        onClose={closeDialog}
      />
    </Fragment>
  );
};
